import { useEffect, useState } from "react";
import { Loader2, AlertCircle, ExternalLink } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { getRecuObjectUrl, getRecuObjectUrlByToken } from "@/lib/api";

/** Source du reçu : back-office/parent authentifié (paiementId) ou lien public (token). */
export type RecuPreviewSource =
  | { kind: "auth"; paiementId: string }
  | { kind: "token"; token: string };

export interface RecuPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: RecuPreviewSource;
  /** Référence affichée (titre / nom de fichier de secours). */
  reference?: string;
}

/**
 * Aperçu modal du reçu PDF — SANS téléchargement préalable. On récupère le PDF
 * en blob (avec le JWT pour le chemin authentifié, avec retry 425/503 pour le
 * lien public), on injecte l'object URL dans une <iframe> (viewer PDF natif du
 * navigateur, zoom inclus), puis on révoque l'URL à la fermeture/démontage.
 */
export default function RecuPreviewDialog({
  open,
  onOpenChange,
  source,
  reference,
}: RecuPreviewDialogProps) {
  const [objUrl, setObjUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // Clé stable identifiant la source — relance le fetch si elle change.
  const sourceKey =
    source.kind === "auth" ? `auth:${source.paiementId}` : `token:${source.token}`;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let createdUrl: string | null = null;
    setLoading(true);
    setError(false);
    setObjUrl(null);

    const fetcher =
      source.kind === "auth"
        ? getRecuObjectUrl(source.paiementId)
        : getRecuObjectUrlByToken(source.token);

    fetcher
      .then((url) => {
        createdUrl = url;
        if (cancelled) {
          // Le dialog s'est refermé avant la résolution → on libère tout de suite.
          URL.revokeObjectURL(url);
          return;
        }
        setObjUrl(url);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
    // sourceKey encode source.kind + l'identifiant : suffisant et stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sourceKey]);

  const title = reference ? `Reçu ${reference}` : "Reçu";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[95vw] h-[90vh] p-0 flex flex-col gap-0 overflow-hidden">
        <div className="shrink-0 border-b border-slate-200 px-5 py-3">
          <DialogTitle className="text-base font-bold text-navy">{title}</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Aperçu du reçu PDF : utilisez les contrôles du lecteur pour zoomer ou
            imprimer.
          </DialogDescription>
        </div>

        <div className="relative flex-1 min-h-0">
          {loading && (
            <div
              className="absolute inset-0 flex items-center justify-center text-slate-500"
              data-testid="recu-preview-loading"
            >
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          )}

          {error && (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center text-slate-600"
              data-testid="recu-preview-error"
            >
              <AlertCircle className="w-10 h-10 text-destructive" />
              <p className="text-sm font-medium">
                Impossible de charger le reçu pour le moment.
              </p>
              <p className="text-xs text-slate-500">
                Le PDF est peut-être encore en cours de génération. Réessayez dans
                quelques instants.
              </p>
            </div>
          )}

          {objUrl && (
            <iframe
              src={objUrl}
              title={title}
              className="w-full h-full"
              data-testid="recu-preview-iframe"
            />
          )}
        </div>

        {objUrl && (
          <div className="shrink-0 border-t border-slate-200 px-5 py-2.5 text-right">
            <a
              href={objUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-navy hover:underline"
              data-testid="recu-preview-open-tab"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Ouvrir dans un onglet
            </a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
